import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { Box, Typography, LinearProgress, Container, CircularProgress, Alert } from '@mui/material';
import Footer from './Footer';
import AdBanner from './AdBanner';

// QueuePage handles queue polling, session claiming, and user feedback for the GT7 Telemetry Dashboard.
export default function QueuePage({ queueId, backendUrl, onReady }) {
  const [position, setPosition] = useState(null);
  const [eta, setEta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingActive, setPollingActive] = useState(true);
  const [queueExited, setQueueExited] = useState(false);

  // Poll the queue for position and eligibility every 5 seconds
  useEffect(() => {
    if (!queueId) return;
    let cancelled = false;
    const pollQueue = async () => {
      try {
        const res = await fetch(`${backendUrl}/queue/${queueId}`);
        if (!res.ok) throw new Error('Network error');
        const data = await res.json();
        setPosition(data.position);
        setEta(data.eta_seconds);
        setLoading(false);
        setError(null);
        // If user is eligible for a session, try to claim it
        if (data.position === 1 && data.session_count < data.max_sessions) {
          const pendingSessionUuid = localStorage.getItem('pending_session_uuid');
          const claimRes = await fetch(`${backendUrl}/claim_session/${queueId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_uuid: pendingSessionUuid })
          });
          if (claimRes.ok) {
            const sessionData = await claimRes.json();
            localStorage.removeItem('queue_id');
            localStorage.removeItem('pending_session_uuid');
            if (onReady) {
              onReady(sessionData.session_uuid, sessionData.token);
            }
            setPollingActive(false);
            return;
          }
        }
        // If user is no longer in the queue, auto-leave
        if (data.position === -1) {
          if (queueId) {
            fetch(`${backendUrl}/queue/${queueId}`, { method: 'DELETE' });
            localStorage.removeItem('queue_id');
            localStorage.removeItem('pending_session_uuid');
          }
          setPollingActive(false);
          setQueueExited(true);
        }
      } catch (e) {
        setError('Lost connection to server. Please check your network.');
        setLoading(false);
        setPollingActive(false);
      }
    };
    if (pollingActive) pollQueue(); // initial call
    const interval = setInterval(() => {
      if (!cancelled && pollingActive) pollQueue();
    }, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [queueId, backendUrl, onReady, pollingActive]);

  // Heartbeat: send keep-alive to backend every 20 seconds
  useEffect(() => {
    if (!queueId) return;
    let cancelled = false;
    const interval = setInterval(() => {
      if (!cancelled && pollingActive) {
        fetch(`${backendUrl}/queue/${queueId}/heartbeat`, { method: 'POST' });
      }
    }, 20000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [queueId, backendUrl, pollingActive]);

  // Retry handler for error or queue exit
  const handleRetry = () => {
    setError(null);
    setQueueExited(false);
    setPollingActive(true);
    setLoading(true);
  };

  return (
    <>
      {/* AdSense Banner - queue page */}
      <AdBanner style={{ marginTop: 24, marginBottom: 24 }} />
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center', background: 'rgba(10,24,51,0.85)', borderRadius: 4, boxShadow: '0 8px 32px 0 rgba(0,0,0,0.25)', p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <img src="/gtt-logo.png" alt="GT Telemetry logo" style={{ height: 250, marginBottom: 12 }} />
          <Typography variant="h3" gutterBottom sx={{
            fontFamily: 'Orbitron, Roboto, Helvetica, Arial, sans-serif',
            fontWeight: 900,
            color: '#fff',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            lineHeight: 1.1,
            mb: 0
          }}>
            Waiting for a Session
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress color="primary" thickness={5} size={48} aria-label="Waiting for session" />
          </Box>
          {/* Main status and queue info */}
          {!queueExited && !error && (
            <>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: '#fff', fontSize: '1.1rem' }}
                role="status"
                aria-live="polite"
              >
                The server is at capacity. You are in a queue.<br />
                {position !== null ? `Your position: ${position}` : "Joining queue..."}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#fff' }}>
                {eta !== null ? `Maximum estimated wait: ${Math.ceil(eta/60)} min` : ''}
              </Typography>
            </>
          )}
          {/* Error state */}
          {error && (
            <>
              <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
              <Box sx={{ mt: 2 }}>
                <button onClick={handleRetry} style={{
                  background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 24px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
                  aria-label="Retry queue polling"
                >
                  Retry
                </button>
              </Box>
            </>
          )}
          {/* Queue exited state */}
          {queueExited && !error && (
            <>
              <Alert severity="info" sx={{ width: '100%' }}>You have been removed from the queue. Please try again later.</Alert>
              <Box sx={{ mt: 2 }}>
                <button onClick={handleRetry} style={{
                  background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 24px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
                  aria-label="Retry queue polling"
                >
                  Retry
                </button>
              </Box>
            </>
          )}
          {loading && <LinearProgress color="primary" sx={{ mb: 2, width: '100%' }} />}
        </Box>
      </Container>
      <Footer />
    </>
  );
}

QueuePage.propTypes = {
  queueId: PropTypes.string,
  backendUrl: PropTypes.string.isRequired,
  onReady: PropTypes.func
};
